import clsx from 'clsx';
import { memo, useCallback, useEffect, useRef } from 'react';
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';

import './virtual-list.scss';

export const VirtualListComponent = <T extends { id?: string }>(props: VirtualListProps<T>) => {
  const {
    data,
    resetKey,
    scrollToById,
    renderer,
    lanes = 1,
    defaultSize,
    overscan = 5,
    onLastItemVisible,
    endReachRows = 0,
    paddingStart,
    paddingEnd,
    loading,
    getItemKey: propGetItemKey,
    ...rest
  } = props;

  const parentRef = useRef<HTMLDivElement>(null);

  // Keep the extractor identity stable across scroll renders but tied to `data`:
  // getMeasurements memoizes on getItemKey identity, so a per-render closure would
  // force an O(n) recompute every scroll frame. Depending on `data` still changes
  // identity on a pure reorder (new array, same length) so keys recompute then.
  // The virtualizer can invoke the key extractor with a stale out-of-range index from its
  // deferred scroll timeout (virtual-core clamps the index at schedule time, not fire time),
  // so fall back to the numeric index like virtual-core's defaultKeyExtractor when data[index]
  // is undefined instead of dereferencing it.
  const getItemKey = useCallback(
    (index: number): string | number => {
      const item = data[index];
      return item === undefined ? index : propGetItemKey!(item);
    },
    [propGetItemKey, data]
  );

  const virtualizer = useVirtualizer({
    overscan,
    lanes,
    paddingEnd: paddingEnd,
    paddingStart: paddingStart,
    getScrollElement: () => parentRef.current,
    count: (data || []).length,
    estimateSize: useCallback(() => defaultSize, [defaultSize]),
    ...(propGetItemKey ? { getItemKey } : {}),
  });

  useEffect(() => {
    if (data.length) {
      virtualizer.scrollToIndex(0);
    }
  }, [resetKey]);

  useEffect(() => {
    if (data.length && scrollToById) {
      const index = data.map((item) => item?.id).indexOf(scrollToById);
      if (index > -1) {
        virtualizer.scrollToIndex(index, { align: 'center' });
      }
    }
  }, [scrollToById, data]);

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    const el = parentRef.current;
    if (!el || !data.length || !onLastItemVisible) return;

    const last = virtualItems.at(-1);
    if (!last) return;

    onLastItemVisible(
      isLastVirtualItemVisible({
        lastIndex: last.index,
        lastEnd: last.end,
        dataLength: data.length,
        scrollTop: el.scrollTop,
        clientHeight: el.clientHeight,
        scrollHeight: el.scrollHeight,
        endReachRows,
      })
    );
  }, [virtualItems, data.length, endReachRows, onLastItemVisible]);

  return (
    <div
      ref={parentRef}
      className={clsx('virtual-list-scroll-holder', {
        'pointer-events-none': loading,
      })}
    >
      <ul
        className="virtual-list"
        data-testid="virtual-list"
        style={{
          height: `${virtualizer.getTotalSize()}px`,
        }}
        {...rest}
      >
        {virtualItems.map((virtualItem) => {
          return renderer(data[virtualItem.index], virtualItem);
        })}
      </ul>
    </div>
  );
};

// memo not working with generic component, error is in consumer of VirtualList (SmartList)
export const VirtualList = memo(VirtualListComponent) as typeof VirtualListComponent;

// Last row counts as visible when it clears the bottom inset, or when the scroll
// container is already at its end. The inset alone is not enough with paddingEnd
// < 48: at max scroll, last.end === viewportBottom.
// Optional endReachRows (Archive prefetch) fires earlier once the last rendered
// virtual index is within that many rows of the list end — default 0 keeps the
// exact last-row behavior for DocuList / source panels.
export const isLastVirtualItemVisible = ({
  lastIndex,
  lastEnd,
  dataLength,
  scrollTop,
  clientHeight,
  scrollHeight,
  bottomInset = 48,
  endReachRows = 0,
}: {
  lastIndex: number;
  lastEnd: number;
  dataLength: number;
  scrollTop: number;
  clientHeight: number;
  scrollHeight: number;
  bottomInset?: number;
  endReachRows?: number;
}): boolean => {
  if (dataLength <= 1) {
    return true;
  }

  if (endReachRows > 0 && lastIndex >= dataLength - 1 - endReachRows) {
    return true;
  }

  if (lastIndex !== dataLength - 1) {
    return false;
  }

  const viewportBottom = scrollTop + clientHeight;
  if (lastEnd <= viewportBottom - bottomInset) {
    return true;
  }

  return scrollHeight - viewportBottom <= 2;
};

interface VirtualListProps<T extends { id?: string }> {
  data: T[];
  loading?: boolean;
  resetKey?: string;
  lanes?: number;
  overscan?: number;
  paddingEnd?: number;
  defaultSize: number;
  scrollToById?: string;
  paddingStart?: number;
  endReachRows?: number;
  onLastItemVisible?: (visible: boolean) => void;
  getItemKey?: (item: T) => string;
  renderer: (item: T, virtualItem: VirtualItem<any>) => React.ReactNode;
}
